import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { BlueprintEmpty, BlueprintGrid, BlueprintLoading, BpFrame, DesignPlate } from "@/components/blueprint/Blueprint";
import { SEOHead } from "@/components/shared/SEOHead";
import { fetchCommunityDesigns } from "@/lib/api";
import { getDesignPreviewUrl, prefetchImages } from "@/lib/imagePrefetch";
import { formatNumber, timeAgo } from "@/lib/utils";
import type { CommunityDesignItem } from "@/types/api";

export const Route = createFileRoute("/gallery")({
  component: GalleryPage,
});

const ProductShowcase = lazy(() =>
  import("@/components/shared/ProductShowcase").then((module) => ({
    default: module.ProductShowcase,
  }))
);

type SortMode = "trending" | "new" | "all";

const SORT_OPTIONS: { value: SortMode; label: string }[] = [
  { value: "trending", label: "Trending" },
  { value: "new", label: "New" },
  { value: "all", label: "All" },
];

function GalleryPage() {
  const [designs, setDesigns] = useState<CommunityDesignItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<SortMode>("trending");
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [submittedQuery, setSubmittedQuery] = useState("");
  const [activeDesign, setActiveDesign] = useState<CommunityDesignItem | null>(null);
  const [showcaseDesign, setShowcaseDesign] = useState<CommunityDesignItem | null>(null);

  const selectedDesign = activeDesign || designs[0] || null;
  const selectedImage = selectedDesign ? getDesignPreviewUrl(selectedDesign) : "";

  const totalSaves = useMemo(
    () => designs.reduce((sum, design) => sum + (design.save_count || 0), 0),
    [designs]
  );

  const loadDesigns = useCallback(
    async (append = false, nextCursor?: string, query = submittedQuery) => {
      if (append) setLoadingMore(true);
      else setLoading(true);

      try {
        const response = await fetchCommunityDesigns({
          sort,
          limit: 40,
          cursor: nextCursor || undefined,
          q: query || undefined,
        });

        setDesigns((current) => append ? [...current, ...response.designs] : response.designs);
        prefetchImages(response.designs.map(getDesignPreviewUrl), append ? 8 : 16);
        setCursor(response.next_cursor);
        setHasMore(response.has_more);
        setError(null);
        if (!append) setActiveDesign(response.designs[0] || null);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : "Failed to load designs");
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [sort, submittedQuery]
  );

  useEffect(() => {
    loadDesigns(false, undefined, submittedQuery);
  }, [loadDesigns, submittedQuery]);

  const handleSearch = () => {
    setSubmittedQuery(searchQuery.trim());
  };

  return (
    <div className="bp-page bp-gallery-page page-enter">
      <SEOHead
        title="The Inkwell — Quuil Community Gallery"
        description="Explore trending and new community artwork in the Quuil Inkwell, save designs, and use them as starting points for real product mockups."
        path="/gallery"
      />

      <section className="bp-section bp-gallery-header">
        <BlueprintGrid />
        <div className="bp-noise" aria-hidden="true" />
        <div className="bp-container">
          <div className="bp-gallery-hero-lockup">
            <div className="bp-section-title">
              <span className="bp-kicker">Discover // Save // Remix</span>
              <div className="bp-gallery-title-row">
                <h1 className="bp-gallery-title">
                  The Inkwell
                </h1>
                <div className="bp-gallery-phone-accent" aria-hidden="true">
                  <img src="/clean-screens/inkwelldark.png" alt="" loading="eager" />
                </div>
              </div>
              <p>
                Discover original work from QUUIL creators, save what catches your eye, and use any community design as a starting point for tees, hoodies, hats, mugs, drinkware, and more.
              </p>
            </div>
          </div>

          <div className="bp-toolbar">
            <div className="bp-search-group">
              <input
                className="bp-search"
                type="search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") handleSearch();
                }}
                placeholder="Search artwork..."
                aria-label="Search designs"
              />
              <button type="button" className="bp-button bp-button-primary shrink-0" onClick={handleSearch}>
                Search
              </button>
            </div>

            <div className="bp-tabs" role="tablist" aria-label="Sort designs">
              {SORT_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="tab"
                  aria-selected={sort === option.value}
                  className={`bp-tab ${sort === option.value ? "is-active" : ""}`}
                  onClick={() => setSort(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bp-section pt-0">
        <BlueprintGrid light />
        <div className="bp-container">
          {loading ? (
            <BlueprintLoading label="Opening the Inkwell" />
          ) : error ? (
            <BlueprintEmpty title="Inkwell Offline" detail={error} />
          ) : designs.length === 0 ? (
            <BlueprintEmpty
              title="No Matching Designs"
              detail={submittedQuery ? `No results for "${submittedQuery}".` : "The Inkwell is quiet right now."}
            />
          ) : (
            <>
              <div className="bp-featured mb-8">
                <BpFrame className="bp-featured-preview" label="SELECTED DESIGN">
                  {selectedImage ? (
                    <button
                      type="button"
                      className="bp-selected-design-canvas"
                      onClick={() => selectedDesign && setShowcaseDesign(selectedDesign)}
                      aria-label={`Open ${selectedDesign?.title || "selected design"}`}
                    >
                      <img src={selectedImage} alt={selectedDesign?.title || "Selected Inkwell design"} />
                    </button>
                  ) : null}
                </BpFrame>

                <BpFrame className="grid content-between gap-6 p-5" label="METADATA">
                  <div>
                    <span className="bp-kicker">Design Data</span>
                    <h2 className="font-[var(--bp-display)] text-5xl uppercase leading-none md:text-7xl">
                      {selectedDesign?.title || "Untitled"}
                    </h2>
                    <p className="mt-4 max-w-lg text-[var(--bp-dim)]">
                      By {selectedDesign?.creator_display_name || "QUUIL creator"} // {selectedDesign ? timeAgo(selectedDesign.created_at) : "now"}
                    </p>
                  </div>
                  <div className="grid gap-3 font-mono text-xs uppercase tracking-[0.12em] text-[var(--bp-dim)] sm:grid-cols-3">
                    <span>{formatNumber(selectedDesign?.save_count || 0)} saves</span>
                    <span>{formatNumber(selectedDesign?.view_count || 0)} views</span>
                    <span>{selectedDesign?.style_name || "mixed media"}</span>
                  </div>
                  <button
                    type="button"
                    className="bp-button bp-button-primary w-fit"
                    onClick={() => selectedDesign && setShowcaseDesign(selectedDesign)}
                  >
                    Use In Mockup
                  </button>
                </BpFrame>
              </div>

              <div className="mb-4 flex items-center justify-between font-mono text-[0.68rem] uppercase tracking-[0.12em] text-[var(--bp-dim)]">
                <span>{formatNumber(designs.length)} designs loaded</span>
                <span>{formatNumber(totalSaves)} community saves</span>
              </div>

              <div className="bp-design-grid">
                {designs.map((design) => (
                  <DesignPlate
                    key={design.id}
                    design={design}
                    active={selectedDesign?.id === design.id}
                    onSelect={() => setActiveDesign(design)}
                  />
                ))}
              </div>

              {hasMore ? (
                <div className="mt-10 flex justify-center">
                  <button
                    type="button"
                    className="bp-button bp-button-ghost"
                    disabled={loadingMore}
                    onClick={() => cursor && loadDesigns(true, cursor)}
                  >
                    {loadingMore ? "Loading..." : "Load More Designs"}
                  </button>
                </div>
              ) : null}
            </>
          )}
        </div>
      </section>

      {showcaseDesign ? (
        <Suspense fallback={null}>
          <ProductShowcase design={showcaseDesign} onClose={() => setShowcaseDesign(null)} />
        </Suspense>
      ) : null}
    </div>
  );
}
